import classNames from "classnames";
import { useCallback, useEffect, useRef } from "react";
import FileHelper from "../../../../helpers/FileHelper";
import { MessageFormStateInterface } from "../../../../interfaces/MessageInterface";
import ResumableTextarea from "../../../atoms/ResumableTextarea";
import { useResumableTextarea } from "../../../atoms/ResumableTextarea/useResumableTextarea";
import Textarea from "../../../atoms/Textarea/index";
import DeprecatedTagButton from "../../../features/DeprecatedTagButton/index";
import styles from "../styles.scss";

interface ContentFieldProps {
  formState?: MessageFormStateInterface;
  // フォームのエラー表示欄にエラーがあるか（API のエラーと、添付のサイズ超過のエラーを合わせたもの）
  hasError: boolean;
  canSendMessage: boolean;
  disabledPlaceholder: string;
  changeFormContent: (content: string) => void;
  changeFormFile: (file: File | null) => void;
  rows?: number;
  storageKey: string;
  textareaMaxHeightBaseElementRef?: React.RefObject<HTMLElement | null>;
}

const RESUME_MESSAGE =
  "編集途中のメッセージを復元しますか？\n（「キャンセル」を選択すると復元内容が破棄されます。）";

const DEFAULT_ROWS = 1;
const FILENAME_MAX_LENGTH = 30;

export const ContentField = ({
  formState,
  hasError,
  storageKey,
  changeFormContent,
  changeFormFile,
  canSendMessage,
  disabledPlaceholder,
  rows = DEFAULT_ROWS,
  textareaMaxHeightBaseElementRef,
}: ContentFieldProps) => {
  const { clearValue: clearContentStorage, ...resumableTextareaProps } =
    useResumableTextarea({
      value: formState?.values.content || "",
      key: storageKey,
      confirmMessage: RESUME_MESSAGE,
      onResume: changeFormContent,
    });

  const prevSubmitting = useRef(formState?.submitting);

  const handleContentChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      changeFormContent(e.target.value);
    },
    [changeFormContent],
  );

  const handleFileDelete = useCallback(() => {
    changeFormFile(null);
  }, [changeFormFile]);

  useEffect(() => {
    if (formState) {
      const wasSubmitting = prevSubmitting.current;
      const isNotSubmitting = !formState.submitting;
      const hasNoErrors = formState.apiErrors.length === 0;

      if (wasSubmitting && isNotSubmitting && hasNoErrors) {
        clearContentStorage();
      }
      prevSubmitting.current = formState.submitting;
    }
  }, [formState, clearContentStorage]);

  const isDisabled = !formState || !canSendMessage;
  const hasFile = formState?.values.file;

  if (isDisabled) {
    return (
      <Textarea
        name="content"
        disabled
        placeholder={disabledPlaceholder}
        value=""
        rows={rows}
        className={styles.content__inputDisabled}
      />
    );
  }

  if (hasFile) {
    return (
      <div
        className={classNames(styles.content__file, {
          [styles.fieldError]: hasError,
        })}
      >
        <DeprecatedTagButton
          label={FileHelper.truncateFilename(hasFile.name, FILENAME_MAX_LENGTH)}
          iconName="icon-close-x"
          theme="lightgray"
          onClick={handleFileDelete}
          dataTestId="common-message-form-file-delete-button"
        />
      </div>
    );
  }

  return (
    <ResumableTextarea
      name="content"
      hasError={hasError}
      placeholder="メッセージを入力"
      value={formState.values.content}
      onChange={handleContentChange}
      rows={rows}
      className={styles.content__input}
      textareaMaxHeightBaseElementRef={textareaMaxHeightBaseElementRef}
      {...resumableTextareaProps}
    />
  );
};
